import React, { useEffect, useMemo, useState } from 'react';
import TaskCard from './Taskcard';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatoDuracion, get_all_members, get_task_time_stats, list_all_users_task } from '../../../utils/talent_services';
import Select, { components } from 'react-select';
import TaskTimePieChart from '../dashboard/TaskTimePieChart';
import Statistics from './Statistics';
import UserAvatar from '../dashboard/UserAvatar';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Accordion, Card, Modal } from 'react-bootstrap';
import { subDays, format } from 'date-fns';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);

const PERIOD_OPTIONS = [
  { value: 7, label: '7 dias' },
  { value: 15, label: '15 dias' },
  { value: 30, label: '30 dias' },
  { value: 60, label: '60 dias' },
];

const WEEKDAY_SHORT = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
const TASK_GROUP_BY = {
  creation: 'creation',
  due: 'due',
  completion: 'completion',
};

const KR_FILTER_NONE = '__NONE__';

function taskKrFilterValue(task) {
  const raw = task?.kr_trello_id;
  const s = raw != null ? String(raw).trim() : '';
  return s ? s : KR_FILTER_NONE;
}

function taskMatchesKrSelection(task, selectedKrTrelloIds) {
  if (!selectedKrTrelloIds || selectedKrTrelloIds.length === 0) return true;
  return selectedKrTrelloIds.includes(taskKrFilterValue(task));
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function lerp(start, end, t) {
  return start + (end - start) * t;
}

function getHourAlertColor(hours) {
  const safeHours = Number(hours) || 0;
  const green = { r: 25, g: 135, b: 84 };   // #198754
  const red = { r: 220, g: 53, b: 69 };     // #dc3545

  let ratio = 0;
  if (safeHours >= 7 && safeHours <= 9) {
    ratio = 0;
  } else if (safeHours < 7) {
    ratio = clamp((7 - safeHours) / 5, 0, 1); // 2h => rojo intenso
  } else {
    ratio = clamp((safeHours - 9) / 3, 0, 1); // 12h => rojo intenso
  }

  const r = Math.round(lerp(green.r, red.r, ratio));
  const g = Math.round(lerp(green.g, red.g, ratio));
  const b = Math.round(lerp(green.b, red.b, ratio));
  return {
    background: `rgba(${r}, ${g}, ${b}, 0.82)`,
    border: `rgba(${r}, ${g}, ${b}, 1)`,
  };
}

function parseDueDateOnlyString(raw) {
  if (raw == null) return null;
  const text = String(raw).trim();
  if (!text) return null;
  const base = text.split('T')[0].split(' ')[0];
  const parts = base.split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((part) => Number(part));
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, 12, 0, 0, 0);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function weekStartMs(dateLike) {
  if (!dateLike) return -1;
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return -1;
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday.getTime();
}

function missingWeekLabel(groupBy) {
  if (groupBy === TASK_GROUP_BY.due) return 'Sin fecha de vencimiento';
  if (groupBy === TASK_GROUP_BY.completion) return 'Sin fecha de terminacion';
  return 'Sin fecha de creacion';
}

function formatWeekLabel(weekStart, groupBy) {
  if (weekStart < 0) return missingWeekLabel(groupBy);
  const start = new Date(weekStart);
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const opts = { day: 'numeric', month: 'short', year: 'numeric' };
  return `Semana: ${start.toLocaleDateString('es-ES', opts)} - ${end.toLocaleDateString('es-ES', opts)}`;
}

function rowWeekAnchor(task, groupBy) {
  if (groupBy === TASK_GROUP_BY.due) {
    const dueDate = parseDueDateOnlyString(task?.due_date);
    if (!dueDate) return '';
    const y = dueDate.getFullYear();
    const m = String(dueDate.getMonth() + 1).padStart(2, '0');
    const d = String(dueDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}T12:00:00`;
  }
  if (groupBy === TASK_GROUP_BY.completion) {
    if (task?.completed_at) return String(task.completed_at);
    return task?.end_date ? String(task.end_date) : '';
  }
  return task?.creation_date ? String(task.creation_date) : '';
}

function compareTasksByDurationWithinGroup(periodTaskMinutes) {
  return (a, b) => {
    const pa = Number(periodTaskMinutes[a.id]) || 0;
    const pb = Number(periodTaskMinutes[b.id]) || 0;
    if (pb !== pa) return pb - pa;
    const ta = Number(a.Tiempo_Activo_Total_Minutos_Enteros) || 0;
    const tb = Number(b.Tiempo_Activo_Total_Minutos_Enteros) || 0;
    if (tb !== ta) return tb - ta;
    return Number(a.id) - Number(b.id);
  };
}

function TaskHoursByDayBarChart({ dailyStats, dailyCompletedStats, loading, error, selectedDayFilter, onSelectDayFilter }) {
  const mergedDailyRows = useMemo(() => {
    const byDay = new Map();

    (dailyStats || []).forEach((row) => {
      if (!row?.day) return;
      byDay.set(row.day, {
        day: row.day,
        day_label: row.day_label,
        total_hours: Number(row.total_hours) || 0,
        completed_tasks: 0,
      });
    });

    (dailyCompletedStats || []).forEach((row) => {
      if (!row?.day) return;
      const existing = byDay.get(row.day);
      if (existing) {
        existing.completed_tasks = Number(row.completed_tasks) || 0;
      } else {
        byDay.set(row.day, {
          day: row.day,
          day_label: row.day_label,
          total_hours: 0,
          completed_tasks: Number(row.completed_tasks) || 0,
        });
      }
    });

    return [...byDay.values()].sort((a, b) => String(a.day).localeCompare(String(b.day)));
  }, [dailyStats, dailyCompletedStats]);

  const chartData = {
    labels: mergedDailyRows.map((row) => {
      const parsedDay = row.day ? new Date(`${row.day}T00:00:00`) : null;
      if (!parsedDay || Number.isNaN(parsedDay.getTime())) {
        return row.day_label || '';
      }
      const weekday = WEEKDAY_SHORT[parsedDay.getDay()] || '';
      const dayLabel = row.day_label || format(parsedDay, 'dd/MM');
      return `${weekday} ${dayLabel}`;
    }),
    datasets: [
      {
        type: 'bar',
        label: 'Horas',
        data: mergedDailyRows.map((row) => row.total_hours),
        backgroundColor: mergedDailyRows.map((row) => {
          if (selectedDayFilter?.mode === 'session' && row.day === selectedDayFilter?.day) {
            return 'rgba(13, 110, 253, 0.95)';
          }
          return getHourAlertColor(row.total_hours).background;
        }),
        borderColor: mergedDailyRows.map((row) => {
          if (selectedDayFilter?.mode === 'session' && row.day === selectedDayFilter?.day) {
            return 'rgba(13, 110, 253, 1)';
          }
          return getHourAlertColor(row.total_hours).border;
        }),
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        type: 'line',
        label: 'Tareas completadas',
        data: mergedDailyRows.map((row) => row.completed_tasks),
        borderColor: 'rgba(25, 135, 84, 1)',
        backgroundColor: 'rgba(25, 135, 84, 0.2)',
        yAxisID: 'yTasks',
        tension: 0.25,
        fill: false,
        pointRadius: mergedDailyRows.map((row) => (
          selectedDayFilter?.mode === 'completion' && row.day === selectedDayFilter?.day ? 6 : 3
        )),
        pointHoverRadius: 5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset?.type === 'line') {
              return `Completadas: ${context.raw || 0}`;
            }
            return `Horas: ${context.raw || 0} h`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `${value}h`,
        },
      },
      yTasks: {
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          precision: 0,
        },
      },
    },
    onClick: (_, elements) => {
      if (!elements || elements.length === 0) return;
      const chartElement = elements[0];
      const clickedDataset = chartData.datasets[chartElement.datasetIndex];
      const clickedRow = mergedDailyRows[chartElement.index];
      if (!clickedRow?.day) return;

      if (clickedDataset?.type === 'bar') {
        if (selectedDayFilter?.mode === 'session' && selectedDayFilter?.day === clickedRow.day) {
          onSelectDayFilter?.(null);
          return;
        }
        onSelectDayFilter?.({ mode: 'session', day: clickedRow.day });
        return;
      }

      if (clickedDataset?.type === 'line') {
        if (selectedDayFilter?.mode === 'completion' && selectedDayFilter?.day === clickedRow.day) {
          onSelectDayFilter?.(null);
          return;
        }
        onSelectDayFilter?.({ mode: 'completion', day: clickedRow.day });
      }
    },
  };

  return (
    <Card className="shadow-sm h-100">
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span className="fw-semibold">Horas por dia</span>
        {selectedDayFilter?.day && (
          <small className="text-primary">
            Filtrando {selectedDayFilter.mode === 'completion' ? 'completadas' : 'sesiones'}: {selectedDayFilter.day}
          </small>
        )}
      </Card.Header>
      <Card.Body>
        {loading && <div className="text-center py-4 text-muted">Cargando...</div>}
        {error && <div className="text-center py-4 text-danger">{error}</div>}
        {!loading && !error && mergedDailyRows.length === 0 && (
          <div className="text-center py-4 text-muted">
            No hay horas registradas para este periodo.
          </div>
        )}
        {!loading && !error && mergedDailyRows.length > 0 && (
          <div style={{ height: '240px' }}>
            <Bar data={chartData} options={options} />
          </div>
        )}
      </Card.Body>
    </Card>
  );
}



const Task_progress = ({token, filter_complete, title, self_cards, member_trello_id, initialEmailId = null, isEmbedded = false}) => {

  const [searchParams, setSearchParams] = useSearchParams();
  const task_id_param = searchParams.get("task_id");
  const email_id_param = searchParams.get("email_id");
  const embeddedEmailFilter = isEmbedded ? String(initialEmailId || '').trim() : '';
  const routeEmailFilter = !isEmbedded ? String(email_id_param || '').trim() : '';
  const effectiveEmailFilter = embeddedEmailFilter || routeEmailFilter;

  const [ListTask, setListTasks] = useState([]);
  const [UsersOptions, setUsersOptions] = useState([]);
  const [selectedSalesUsr, setselectedSalesUsr] = useState(() =>
    effectiveEmailFilter ? [effectiveEmailFilter] : []
  );
  const [selectedKrTrelloIds, setSelectedKrTrelloIds] = useState([]);
  const [taskTitleSearch, setTaskTitleSearch] = useState('');
  const [showCompletedOnly, setShowCompletedOnly] = useState(true);
  const [taskGroupBy, setTaskGroupBy] = useState(TASK_GROUP_BY.creation);
  const [periodDays, setPeriodDays] = useState(15);
  const [periodTaskMinutes, setPeriodTaskMinutes] = useState({});
  const [dayTaskMinutesMap, setDayTaskMinutesMap] = useState({});
  const [completedTaskDayMap, setCompletedTaskDayMap] = useState({});
  const [dailyStats, setDailyStats] = useState([]);
  const [dailyCompletedStats, setDailyCompletedStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [selectedDayFilter, setSelectedDayFilter] = useState(null);
  const [selectedPieTaskId, setSelectedPieTaskId] = useState(null);
  const [selectedTaskForStats, setSelectedTaskForStats] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [finalSelfcards, setFinalSelfcards] = useState([]);

  const getDisplayName = (row, memberByUsrId = {}) => {
    const memberData = memberByUsrId[row?.usr_id];
    const memberNombre = memberData?.nombre ?? memberData?.Nombre;
    const candidateName = String(row?.usr_name ?? '').trim();
    if (candidateName) return candidateName;
    const teamUsrName = String(row?.team_usr_name ?? '').trim();
    if (teamUsrName) return teamUsrName;
    const fullNameFromMembers = String(memberData?.team_usr_name ?? memberNombre ?? memberData?.usr_name ?? '').trim();
    if (fullNameFromMembers) return fullNameFromMembers;
    const email = String(row?.email_id ?? '').trim();
    if (!email) return 'Usuario';
    return email.split('@')[0];
  };

  const krFilterOptions = useMemo(() => {
    const byKey = new Map();
    ListTask.forEach((t) => {
      const value = taskKrFilterValue(t);
      if (byKey.has(value)) return;
      if (value === KR_FILTER_NONE) {
        byKey.set(value, { value, label: 'Sin KR' });
        return;
      }
      const name = t.kr_name != null ? String(t.kr_name).trim() : '';
      const trello = String(t.kr_trello_id || '').trim();
      const label = name || (trello.length > 14 ? `KR …${trello.slice(-10)}` : `KR (${trello})`);
      byKey.set(value, { value, label });
    });
    return [...byKey.values()].sort((a, b) => {
      if (a.value === KR_FILTER_NONE) return -1;
      if (b.value === KR_FILTER_NONE) return 1;
      return String(a.label).localeCompare(String(b.label), 'es');
    });
  }, [ListTask]);


     useEffect(() => {
               fetch_load_task();
              }, [token, periodDays, taskGroupBy, filter_complete]); 

              useEffect(() => {
     
               //Filtro las tarjetas de estrategias a las que pertenezco 
               if (member_trello_id && self_cards){
                  setFinalSelfcards( self_cards.filter((row) => 
                      row.members && row.members.includes(member_trello_id)
                    ));
                    console.log ("self Cards: ", self_cards);
                    }

              }, [self_cards]); 

  const navigate = useNavigate();   
  const handleOpenTaskStats = (task) => {
    if (!task?.id) return;
    setSelectedTaskForStats(task);
  };

  const handleCloseTaskStats = () => {
    setSelectedTaskForStats(null);
  };

  const handleRefreshData = async () => {
    await fetch_load_task();
    setRefreshKey((prev) => prev + 1);
  };

  async function fetch_load_task() {
              console.log("Cargando tareas...", token);
                const task_list = await list_all_users_task(token, filter_complete, periodDays, taskGroupBy);
                if (task_list==null){
                  navigate("/");
                }else {
                   if(task_list!=null){
                    const memberList = await get_all_members(token);
                    if (memberList === 'INVALID'){
                      navigate("/");
                      return;
                    }
                    const memberByUsrId = Array.isArray(memberList)
                      ? memberList.reduce((acc, memberRow) => {
                          const uid = Number(memberRow?.usr_id);
                          if (Number.isFinite(uid) && uid > 0) {
                            acc[uid] = memberRow;
                          }
                          return acc;
                        }, {})
                      : {};
                   
                    const tempCH = task_list['all_tasks'].map((celement, index) => {
                    return {
                      id: celement.id,
                      task_name: celement.task_name,
                      trello_card_id: celement.trello_card_id,
                      usr_id: celement.usr_id,
                      creation_date: celement.creation_date,
                      completed: celement.completed == 1 ? true : false,
                      active: celement.active == 1 ? true : false,
                      title: celement.title,
                      shortURL: celement.shortURL,
                      kr_trello_id: celement.kr_trello_id,
                      kr_id: celement.kr_id ?? null,
                      kr_name: celement.kr_name != null && String(celement.kr_name).trim() !== ''
                        ? String(celement.kr_name).trim()
                        : null,
                      email_id: celement.email_id,
                      timing: celement.timing,
                      vote_done: celement.vote_done,
                      qlty: celement.qlty,
                      due_date: celement.due_date,
                      end_date: celement.end_date,
                      completed_at: celement.completed_at || celement.end_date || null,
                      Tiempo_Activo_Total_Minutos_Enteros: celement.Tiempo_Activo_Total_Minutos_Enteros,
                      index:index
                    };
                  });
                    setListTasks(tempCH);


                    const tempUsr = task_list['all_tasks'].map((celement, index) => {
                    return {
                      value: celement.email_id,
                      label: getDisplayName(celement, memberByUsrId),
                      email_id: celement.email_id,
                      usr_id: celement.usr_id,
                      index:index
                    };
                  });
                    
  
                   const seenIds = new Set();
                  const uniqueUsersOptions = tempUsr.filter(option => {
                      const isDuplicate = seenIds.has(option.value);
                      seenIds.add(option.value);
                      return !isDuplicate;
                  });

                  if (effectiveEmailFilter && !uniqueUsersOptions.some(option => option.value === effectiveEmailFilter)) {
                    uniqueUsersOptions.push({
                      value: effectiveEmailFilter,
                      label: effectiveEmailFilter
                    });
                  }

                  setUsersOptions(uniqueUsersOptions);
                }
              }
      };
  const baseRows = useMemo(() => {
    let result = [...ListTask];
    const normalizedTaskTitleSearch = taskTitleSearch.trim().toLowerCase();

    
          if (task_id_param){
            console.log("ID TO BE FILTERED&&&&& ",task_id_param);
             result = result.filter((r) => {
                      return task_id_param==r.id;
                    });
          }

          if (showCompletedOnly) {
            result = result.filter((r) => r.completed);
          }

          if (selectedSalesUsr.length > 0) {
            result = result.filter((r) => {
                      return selectedSalesUsr.includes(r.email_id);
                    });
          }

          if (selectedKrTrelloIds.length > 0) {
            result = result.filter((r) => taskMatchesKrSelection(r, selectedKrTrelloIds));
          }

          if (normalizedTaskTitleSearch) {
            result = result.filter((r) => {
              const taskName = String(r.task_name || '').toLowerCase();
              return taskName.includes(normalizedTaskTitleSearch);
            });
          }

        return result;

     }, [selectedSalesUsr, selectedKrTrelloIds, taskTitleSearch, ListTask, showCompletedOnly, task_id_param]);
 
  const chartRows = useMemo(() => {
    let result = [...ListTask];
    const normalizedTaskTitleSearch = taskTitleSearch.trim().toLowerCase();

    if (task_id_param) {
      result = result.filter((r) => task_id_param == r.id);
    }

    if (selectedSalesUsr.length > 0) {
      result = result.filter((r) => selectedSalesUsr.includes(r.email_id));
    }

    if (selectedKrTrelloIds.length > 0) {
      result = result.filter((r) => taskMatchesKrSelection(r, selectedKrTrelloIds));
    }

    if (normalizedTaskTitleSearch) {
      result = result.filter((r) => {
        const taskName = String(r.task_name || '').toLowerCase();
        return taskName.includes(normalizedTaskTitleSearch);
      });
    }

    return result;
  }, [ListTask, selectedSalesUsr, selectedKrTrelloIds, taskTitleSearch, task_id_param]);
 
  const baseRowsTaskIdsKey = useMemo(
    () => chartRows.map((task) => task.id).join(','),
    [chartRows]
  );

  useEffect(() => {
    if (!token) return;

    const taskIds = chartRows.map((task) => task.id);
    if (taskIds.length === 0) {
      setPeriodTaskMinutes({});
      setDayTaskMinutesMap({});
      setCompletedTaskDayMap({});
      setDailyStats([]);
      setDailyCompletedStats([]);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    let cancelled = false;
    setStatsLoading(true);
    setStatsError(null);
    const endDate = new Date();
    const startDate = subDays(endDate, periodDays);
    const dateFrom = format(startDate, 'yyyy-MM-dd 00:00:00');
    const dateTo = format(endDate, 'yyyy-MM-dd 23:59:59');

    get_task_time_stats(token, dateFrom, dateTo, null, taskIds)
      .then((data) => {
        if (cancelled) return;
        if (data === null || data === 'INVALID') {
          setPeriodTaskMinutes({});
          setDayTaskMinutesMap({});
          setCompletedTaskDayMap({});
          setDailyStats([]);
          setDailyCompletedStats([]);
          setStatsError('No se pudieron cargar las horas por dia');
          return;
        }
        const nextMinutes = {};
        (data.stats || []).forEach((stat) => {
          nextMinutes[stat.task_id] = Number(stat.total_minutes) || 0;
        });
        setPeriodTaskMinutes(nextMinutes);

        const nextDayTaskMap = {};
        (data.daily_task_stats || []).forEach((entry) => {
          const dayKey = entry.day;
          const taskId = Number(entry.task_id);
          const minutes = Number(entry.total_minutes) || 0;
          if (!dayKey || !taskId) return;
          if (!nextDayTaskMap[dayKey]) nextDayTaskMap[dayKey] = {};
          nextDayTaskMap[dayKey][taskId] = minutes;
        });
        setDayTaskMinutesMap(nextDayTaskMap);

        const nextCompletedTaskDayMap = {};
        (data.daily_completed_task_stats || []).forEach((entry) => {
          const dayKey = entry.day;
          const taskId = Number(entry.task_id);
          if (!dayKey || !taskId) return;
          if (!nextCompletedTaskDayMap[dayKey]) nextCompletedTaskDayMap[dayKey] = {};
          nextCompletedTaskDayMap[dayKey][taskId] = 1;
        });
        setCompletedTaskDayMap(nextCompletedTaskDayMap);
        setDailyStats(Array.isArray(data.daily_stats) ? data.daily_stats : []);
        setDailyCompletedStats(Array.isArray(data.daily_completed_stats) ? data.daily_completed_stats : []);
      })
      .catch(() => {
        if (!cancelled) {
          setPeriodTaskMinutes({});
          setDayTaskMinutesMap({});
          setCompletedTaskDayMap({});
          setDailyStats([]);
          setDailyCompletedStats([]);
          setStatsError('Error al cargar las horas por dia');
        }
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, periodDays, baseRowsTaskIdsKey, refreshKey, chartRows]);

  useEffect(() => {
    if (!selectedDayFilter?.day || !selectedDayFilter?.mode) return;
    const mapForMode = selectedDayFilter.mode === 'completion' ? completedTaskDayMap : dayTaskMinutesMap;
    if (!mapForMode[selectedDayFilter.day]) {
      setSelectedDayFilter(null);
    }
  }, [dayTaskMinutesMap, completedTaskDayMap, selectedDayFilter]);

  useEffect(() => {
    if (!selectedPieTaskId) return;
    if (!baseRows.some((task) => Number(task.id) === Number(selectedPieTaskId))) {
      setSelectedPieTaskId(null);
    }
  }, [baseRows, selectedPieTaskId]);

  const rows = useMemo(() => {
    const filteredBySelectedDay = selectedDayFilter?.day
      ? baseRows.filter((task) => {
          if (selectedDayFilter.mode === 'completion') {
            return (completedTaskDayMap[selectedDayFilter.day]?.[task.id] || 0) > 0;
          }
          return (dayTaskMinutesMap[selectedDayFilter.day]?.[task.id] || 0) > 0;
        })
      : baseRows;
    const filteredByPieTask = selectedPieTaskId
      ? filteredBySelectedDay.filter((task) => Number(task.id) === Number(selectedPieTaskId))
      : filteredBySelectedDay;
    filteredByPieTask.sort((a, b) => (periodTaskMinutes[b.id] || 0) - (periodTaskMinutes[a.id] || 0));

    // Always keep running tasks first, regardless of current filters.
    if (task_id_param) {
      return filteredByPieTask;
    }

    const runningTasks = [...ListTask]
      .filter((task) => {
        if (!(task.active && !task.completed)) return false;
        if (selectedSalesUsr.length > 0 && !selectedSalesUsr.includes(task.email_id)) {
          return false;
        }
        if (selectedKrTrelloIds.length > 0 && !taskMatchesKrSelection(task, selectedKrTrelloIds)) {
          return false;
        }
        if (taskTitleSearch.trim()) {
          const taskName = String(task.task_name || '').toLowerCase();
          if (!taskName.includes(taskTitleSearch.trim().toLowerCase())) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => (periodTaskMinutes[b.id] || 0) - (periodTaskMinutes[a.id] || 0));

    const runningTaskIds = new Set(runningTasks.map((task) => Number(task.id)));
    const remainingFilteredTasks = filteredByPieTask.filter((task) => !runningTaskIds.has(Number(task.id)));
    return [...runningTasks, ...remainingFilteredTasks];
  }, [baseRows, periodTaskMinutes, selectedDayFilter, dayTaskMinutesMap, completedTaskDayMap, selectedPieTaskId, ListTask, task_id_param, selectedSalesUsr, selectedKrTrelloIds, taskTitleSearch]);
  const pieTaskIds = useMemo(
    () => [...new Set(chartRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id) && id > 0))].sort((a, b) => a - b),
    [chartRows]
  );

  const groupedRows = useMemo(() => {
    const byWeek = new Map();
    rows.forEach((task) => {
      const anchor = rowWeekAnchor(task, taskGroupBy);
      const weekStart = weekStartMs(anchor);
      if (!byWeek.has(weekStart)) byWeek.set(weekStart, []);
      byWeek.get(weekStart).push(task);
    });

    const orderedWeekKeys = [...byWeek.keys()].sort((a, b) => {
      if (a < 0) return 1;
      if (b < 0) return -1;
      return b - a;
    });

    const sortWithinGroup = compareTasksByDurationWithinGroup(periodTaskMinutes);

    return orderedWeekKeys.map((weekStart) => ({
      weekStart,
      label: formatWeekLabel(weekStart, taskGroupBy),
      tasks: [...(byWeek.get(weekStart) || [])].sort(sortWithinGroup),
    }));
  }, [rows, taskGroupBy, periodTaskMinutes]);


const CustomOption = (props) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <UserAvatar usr_id={data.usr_id} usr_name={data.label} />
        <span>{data.label}</span>
      </div>
    </components.Option>
  );
};

const CustomMultiValueLabel = (props) => {
  const { data } = props;

  return (
    <components.MultiValueLabel {...props}>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        <UserAvatar usr_id={data.usr_id} usr_name={data.label} size={20} />
      </span>
    </components.MultiValueLabel>
  );
};
 const getsalesUsrByValue = (values) => {
    if (!Array.isArray(values)) {
      return []; // Ensure it's always an array for multi-select
    }
    return UsersOptions.filter(option => values.includes(option.value));
  };

  const getKrOptionsByValue = (values) => {
    if (!Array.isArray(values)) return [];
    return krFilterOptions.filter((option) => values.includes(option.value));
  };

  useEffect(() => {
    if (!effectiveEmailFilter) return;
    setselectedSalesUsr((prev) => (
      prev.length === 1 && prev[0] === effectiveEmailFilter ? prev : [effectiveEmailFilter]
    ));
  }, [effectiveEmailFilter]);


  return (
   <div className="min-h-screen bg-gray-100 flex flex-col items-center py-4 **space-y-4** w-full">
      {!task_id_param &&
        <div className="col-md-12 mb-3 px-2 w-full max-w-4xl">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 12rem', minWidth: '10rem', maxWidth: '100%' }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar tarea..."
                value={taskTitleSearch}
                onChange={(e) => setTaskTitleSearch(e.target.value)}
                aria-label="Buscar tareas por titulo"
              />
            </div>
            {!isEmbedded && (
              <div style={{ flex: '0 0 auto', width: 'fit-content', minWidth: '12rem' }}>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={UsersOptions}
                  isLoading={UsersOptions.length === 0}
                  isMulti
                  onChange={(selectedOptions) => {
                    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                    setselectedSalesUsr(values);
                  }}
                  placeholder="Usuario..."
                  value={getsalesUsrByValue(selectedSalesUsr)}
                  components={{
                    Option: CustomOption,
                    MultiValueLabel: CustomMultiValueLabel,
                  }}
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      width: 'auto',
                      minWidth: '12rem',
                    }),
                    control: (provided) => ({
                      ...provided,
                      minHeight: '31px',
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      paddingTop: 0,
                      paddingBottom: 0,
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      alignItems: 'center',
                      minHeight: '20px',
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: 0,
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    })
                  }}
                />
              </div>
            )}
            <div style={{ flex: '0 0 11rem', width: '11rem', minWidth: '11rem', maxWidth: '11rem' }}>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={krFilterOptions}
                isMulti
                isClearable
                onChange={(selectedOptions) => {
                  const values = selectedOptions ? selectedOptions.map((option) => option.value) : [];
                  setSelectedKrTrelloIds(values);
                }}
                placeholder="KR..."
                value={getKrOptionsByValue(selectedKrTrelloIds)}
                styles={{
                  container: (provided) => ({
                    ...provided,
                    width: '100%',
                    minWidth: 0,
                  }),
                  control: (provided) => ({
                    ...provided,
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap', margin: 0, flex: '0 0 auto' }}>
              <span>Agrupar:</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', minWidth: '11rem' }}
                value={taskGroupBy}
                onChange={(e) => {
                  const value = e.target.value;
                  setTaskGroupBy(value);
                  if (value === TASK_GROUP_BY.completion) {
                    setShowCompletedOnly(true);
                  }
                }}
                aria-label="Agrupar tareas por"
              >
                <option value={TASK_GROUP_BY.creation}>Fecha de creacion</option>
                <option value={TASK_GROUP_BY.due}>Fecha de vencimiento</option>
                <option value={TASK_GROUP_BY.completion}>Fecha de terminacion</option>
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap', margin: 0, flex: '0 0 auto' }}>
              <input
                type="checkbox"
                checked={showCompletedOnly}
                disabled={taskGroupBy === TASK_GROUP_BY.completion}
                onChange={(e) => setShowCompletedOnly(e.target.checked)}
              />
              Done
            </label>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', flex: '0 0 auto' }}
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value))}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              style={{ flex: '0 0 auto' }}
              onClick={handleRefreshData}
              title="Refrescar datos"
              aria-label="Refrescar datos"
            >
              <i className="bi bi-arrow-clockwise" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      }

             {!task_id_param && (
              <div className="w-full mb-4 px-2">
                <div className="row g-3">
                  <div className="col-12 col-xl-4">
                    <TaskTimePieChart
                      token={token}
                      taskIds={pieTaskIds}
                      title="Tiempo por tarea (lista filtrada)"
                      periodDays={periodDays}
                      showPeriodSelector={false}
                      selectedTaskId={selectedPieTaskId}
                      onSelectTaskId={setSelectedPieTaskId}
                    />
                  </div>
                  <div className="col-12 col-xl-8">
                    <TaskHoursByDayBarChart
                      dailyStats={dailyStats}
                      dailyCompletedStats={dailyCompletedStats}
                      loading={statsLoading}
                      error={statsError}
                      selectedDayFilter={selectedDayFilter}
                      onSelectDayFilter={setSelectedDayFilter}
                    />
                  </div>
                </div>
              </div>
             )}

       <div className="w-full px-2">
        <Accordion flush defaultActiveKey={groupedRows.length > 0 ? ['0'] : []} alwaysOpen>
          {groupedRows.map((weekGroup, weekIndex) => (
            <Accordion.Item eventKey={String(weekIndex)} key={`week-${weekGroup.weekStart}-${weekIndex}`}>
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100 pe-2">
                  <span>{weekGroup.label}</span>
                  <span className="badge bg-secondary">{weekGroup.tasks.length}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="px-0">
                {weekGroup.tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    title={task.task_name}
                    strategy={task.title}
                    usr_id={task.usr_id}
                    usr_name={task.email_id}
                    active={task.active}
                    completed={task.completed}
                    task_id={task.id}
                    token={token}
                    timing={task.timing}
                    vote_done={task.vote_done}
                    qlty={task.qlty}
                    shortURL={task.shortURL}
                    kr_trello_id={task.kr_trello_id}
                    views={0}
                    end_date={task.end_date}
                    onRefresh={()=>{
                      fetch_load_task();
                    }}
                    onOpenStatistics={() => handleOpenTaskStats(task)}
                    duration={formatoDuracion(task.Tiempo_Activo_Total_Minutos_Enteros)}
                  />
                ))}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
      <Modal
        show={Boolean(selectedTaskForStats)}
        onHide={handleCloseTaskStats}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Detalle de tarea</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {selectedTaskForStats && (
            <Statistics
              usr_id={selectedTaskForStats.usr_id}
              token={token}
              cards={finalSelfcards}
              taskId={selectedTaskForStats.id}
              isEmbedded={true}
              onCloseEmbedded={handleCloseTaskStats}
            />
          )}
        </Modal.Body>
      </Modal>
    
    </div>
  );
};

export default Task_progress;